import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import { APP_API_URL } from "../../../utils";
import OrderLayout from "../../../layout/order-layout";
import Loader from "../../../components/loader";
import Invoice from "./invoice";
import { isBrokenTileSaleValidated } from "../../../utils/broken-tile-sale";

export default function ValidateBrokenTiles() {
  const navigate = useNavigate();
  const { ref } = useParams();
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [data, setData] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [libelle, setLibelle] = useState('');
  const [montant, setMontant] = useState('');

  // Fonction pour récupérer les données de la vente de carreaux cassés
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/broken-tiles/sales/${ref}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setData(response.data.broken_tile || response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération de la vente :', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de charger les données de la vente.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ref) {
      fetchData();
    }
  }, [ref]);

  // Valider la vente (génère la facture EMCeF + déduit le stock)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${APP_API_URL}/broken-tiles/validate`, {
        sale_id: data.id,
        validated_by_id: user.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        await fetchData(); // Recharger les données (emecef_data sera maintenant présent)

        Swal.fire({
          icon: 'success',
          title: 'Vente validée !',
          text: response.data.message,
          showConfirmButton: false,
          timer: 2000
        });
      }
    } catch (error) {
      if (error.response?.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur de validation',
          text: error.response?.data?.error || 'Des erreurs ont été détectées.',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: error.response?.data?.error || 'Rupture de stock ou erreur serveur',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Annuler la validation (remet le stock + annule la facture EMCeF)
  const handleCancelOrder = () => {
    if (user.role !== "Administrateur") {
      Swal.fire({
        icon: 'error',
        title: 'Accès refusé',
        text: "Seul un administrateur peut annuler cette vente.",
      });
      return;
    }

    Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: "Voulez-vous vraiment annuler la validation de cette vente ? Le stock sera rétabli et la facture annulée.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Non, revenir',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${APP_API_URL}/broken-tiles/cancel`, {
            sale_id: data.id,
            user_id: user.id
          }, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.status === 200) {
            await fetchData();
            Swal.fire({
              icon: 'success',
              title: 'Validation annulée',
              text: response.data.message,
              showConfirmButton: false,
              timer: 2000
            });
          }
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: error.response?.data?.error || 'Une erreur est survenue lors de l’annulation.',
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleAddDepense = async (e) => {
    e.preventDefault();
    if (!libelle || !montant) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Veuillez remplir tous les champs.',
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${APP_API_URL}/broken-tiles/${data.id}/depenses`, {
        libelle,
        montant,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Dépense ajoutée !',
          text: response.data.message,
          showConfirmButton: false,
          timer: 1500
        });
        setShowModal(false);
        setLibelle('');
        setMontant('');
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Une erreur est survenue lors de l’ajout de la dépense.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const previousTitle = document.title;
    document.title = `ASEBât - ${data.reference_piece || 'Vente'} - Vente de carreaux cassés`;

    window.print();
    document.title = previousTitle;
  };

  if (isLoading) {
    return <Loader />;
  }

  const isValidated = isBrokenTileSaleValidated(data);

  return (
    <OrderLayout>
      <div className="mx-auto w-full max-w-6xl bg-white p-4 sm:p-6 lg:p-8">
        <div className="printable w-full">
        
          <Invoice data={data} isCopy={false} />
          <Invoice data={data} />

          <div className="modal-footer py-4 space-x-4 no-print">
            {/* Bouton Valider → visible seulement si pas encore validée */}
            {!isValidated ? (
              <form onSubmit={handleSubmit}>
                {isLoading ? (
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
                  >
                    <svg
                      aria-hidden="true"
                      role="status"
                      className="inline w-4 h-4 me-2 text-white animate-spin"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                    </svg>
                    Validation en cours...
                  </button>
                ) : (
                  !isRole13 && (
                    <button
                      type="submit"
                      className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
                    >
                      Valider la vente
                    </button>
                  )
                )}
              </form>
            ) : (
              /* Bouton Annuler → visible seulement si validée et admin */
              user.role === "Administrateur" && !isRole13 && (
                <button
                  type="button"
                  onClick={handleCancelOrder}
                  className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-all duration-300"
                >
                  Annuler la validation
                </button>
              )
            )}

            {/* Bouton Imprimer → visible seulement après validation */}
            {isValidated && (
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
              >
                Imprimer
              </button>
            )}

            {!isRole13 && (
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
              >
                Ajouter Dépense
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
            >
              Retour
            </button>
          </div>
        </div>
      </div>

      {/* Modal pour ajouter dépense */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Ajouter une Dépense</h2>
            <form onSubmit={handleAddDepense}>
              <div className="mb-4">
                <label htmlFor="libelle" className="block text-sm font-medium text-gray-700">
                  Libellé
                </label>
                <input
                  type="text"
                  id="libelle"
                  value={libelle}
                  onChange={(e) => setLibelle(e.target.value)}
                  className="form-control"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="montant" className="block text-sm font-medium text-gray-700">
                  Montant
                </label>
                <input
                  type="number"
                  id="montant"
                  value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  className="form-control"
                  min="0"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="inline-flex items-center rounded-md bg-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-400"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                >
                  {isLoading ? 'Ajout en cours...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </OrderLayout>
  );
}